import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { unitsApiService, UnitCreateDTO, UnitUpdateDTO, UnitFilters } from '../api/units-api';
import { CondominiumUnit, UnitStats, UnitCharacteristics } from '../types/unit.types';

// Estado inicial
interface UnitsState {
  units: CondominiumUnit[];
  currentUnit: CondominiumUnit | null;
  loading: boolean;
  error: string | null;
  generating: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: UnitFilters;
  stats: UnitStats | null;
  bulkGenerateResult: {
    totalGenerated: number;
    created: number;
    duplicates: number;
    errors: Array<{ tower: string; apartment: string; error: string }>;
  } | null;
}

const initialState: UnitsState = {
  units: [],
  currentUnit: null,
  loading: false,
  error: null,
  generating: false,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
  stats: null,
  bulkGenerateResult: null,
};

// Async Thunks
export const fetchUnits = createAsyncThunk(
  'units/fetchUnits',
  async (filters: UnitFilters = {}, { rejectWithValue }) => {
    try {
      const response = await unitsApiService.getUnits(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar unidades'
      );
    }
  }
);

export const fetchUnit = createAsyncThunk(
  'units/fetchUnit',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await unitsApiService.getUnitById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar unidad'
      );
    }
  }
);

export const createUnit = createAsyncThunk(
  'units/createUnit',
  async (data: UnitCreateDTO, { rejectWithValue }) => {
    try {
      const response = await unitsApiService.createUnit(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear unidad'
      );
    }
  }
);

export const updateUnit = createAsyncThunk(
  'units/updateUnit',
  async (
    { id, data }: { id: string; data: UnitUpdateDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await unitsApiService.updateUnit(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar unidad'
      );
    }
  }
);

export const deleteUnit = createAsyncThunk(
  'units/deleteUnit',
  async (id: string, { rejectWithValue }) => {
    try {
      await unitsApiService.deleteUnit(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar unidad'
      );
    }
  }
);

export const fetchUnitStats = createAsyncThunk(
  'units/fetchUnitStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await unitsApiService.getUnitStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas'
      );
    }
  }
);

export const bulkGenerateUnits = createAsyncThunk(
  'units/bulkGenerate',
  async (
    data: {
      towerType: 'tower' | 'manzana' | 'etapa' | 'interior';
      towerCount: number;
      apartmentsPerTower: number;
      numberingPattern: 'sequential' | 'floor-apartment' | 'custom';
      customPattern?: string;
      apartmentsPerFloor?: number;
      floorsPerTower?: number;
      unitType: 'apartment' | 'house' | 'commercial' | 'local' | 'parking' | 'storage';
      initialStatus: 'occupied' | 'vacant' | 'maintenance' | 'reserved';
      baseArea?: number;
      baseCharacteristics?: UnitCharacteristics;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await unitsApiService.bulkGenerate(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al generar unidades masivamente'
      );
    }
  }
);

// Slice
const unitsSlice = createSlice({
  name: 'units',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<UnitFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {};
    },
    setCurrentUnit: (state, action: PayloadAction<CondominiumUnit | null>) => {
      state.currentUnit = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    clearBulkGenerateResult: state => {
      state.bulkGenerateResult = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
  },
  extraReducers: builder => {
    // Fetch units
    builder
      .addCase(fetchUnits.pending, state => {
        if (state.units.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.units = payload.units || [];
        
        const newPage = payload.page || 1;
        const newLimit = payload.limit || 10;
        const newTotal = payload.total || 0;
        const newTotalPages = payload.totalPages || 1;
        
        if (
          state.pagination.page !== newPage ||
          state.pagination.limit !== newLimit ||
          state.pagination.total !== newTotal ||
          state.pagination.totalPages !== newTotalPages
        ) {
          state.pagination = {
            page: newPage,
            limit: newLimit,
            total: newTotal,
            totalPages: newTotalPages,
          };
        }
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch unit
    builder
      .addCase(fetchUnit.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUnit = action.payload as CondominiumUnit;
      })
      .addCase(fetchUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create unit
    builder
      .addCase(createUnit.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUnit.fulfilled, (state, action) => {
        state.loading = false;
        const newUnit = action.payload as CondominiumUnit;
        state.units.unshift(newUnit);
        state.pagination.total += 1;
      })
      .addCase(createUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update unit
    builder
      .addCase(updateUnit.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUnit.fulfilled, (state, action) => {
        state.loading = false;
        const updatedUnit = action.payload as CondominiumUnit;
        const index = state.units.findIndex(
          unit => unit.id === updatedUnit.id
        );
        if (index !== -1) {
          state.units[index] = updatedUnit;
        }
        if (state.currentUnit?.id === updatedUnit.id) {
          state.currentUnit = updatedUnit;
        }
      })
      .addCase(updateUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete unit
    builder
      .addCase(deleteUnit.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units = state.units.filter(
          unit => unit.id !== action.payload
        );
        state.pagination.total -= 1;
        if (state.currentUnit?.id === action.payload) {
          state.currentUnit = null;
        }
      })
      .addCase(deleteUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch unit stats
    builder
      .addCase(fetchUnitStats.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnitStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload as UnitStats;
      })
      .addCase(fetchUnitStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Bulk generate units
    builder
      .addCase(bulkGenerateUnits.pending, state => {
        state.generating = true;
        state.error = null;
        state.bulkGenerateResult = null;
      })
      .addCase(bulkGenerateUnits.fulfilled, (state, action) => {
        state.generating = false;
        state.bulkGenerateResult = action.payload as any;
        // Actualizar total de unidades
        if (state.bulkGenerateResult) {
          state.pagination.total += state.bulkGenerateResult.created;
        }
      })
      .addCase(bulkGenerateUnits.rejected, (state, action) => {
        state.generating = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentUnit,
  clearError,
  clearBulkGenerateResult,
  setPagination,
} = unitsSlice.actions;

// Selectors
export const selectUnits = (state: { units: UnitsState }) => state.units.units;
export const selectCurrentUnit = (state: { units: UnitsState }) => state.units.currentUnit;
export const selectUnitsLoading = (state: { units: UnitsState }) => state.units.loading;
export const selectUnitsGenerating = (state: { units: UnitsState }) => state.units.generating;
export const selectUnitsError = (state: { units: UnitsState }) => state.units.error;
export const selectUnitsPagination = (state: { units: UnitsState }) => state.units.pagination;
export const selectUnitsFilters = (state: { units: UnitsState }) => state.units.filters;
export const selectUnitsStats = (state: { units: UnitsState }) => state.units.stats;
export const selectBulkGenerateResult = (state: { units: UnitsState }) => state.units.bulkGenerateResult;

export default unitsSlice.reducer;
